import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { buildApiUrl, getApiHeaders } from '@/config/api';
import { 
  User, 
  GraduationCap, 
  Briefcase, 
  Award, 
  FileText,
  Zap
} from 'lucide-react';
import { apiRequest } from '@/config/api';

import BasicInfoTab from './basic-info/BasicInfoTab';
import EducationTab from './education/EducationTab';
import WorkExperienceTab from './work-experience/WorkExperienceTab';
import SkillsTab from './skills/SkillsTab';
import AwardsTab from './awards/AwardsTab';
import CertificationsTab from './certifications/CertificationsTab';

// Toast notification function with visual feedback
const toast = {
  success: (message: string) => {
    console.log('✅ Success:', message);
    // Create and show success toast
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 4000);
  },
  error: (message: string) => {
    console.error('❌ Error:', message);
    // Create and show error toast
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 5000);
  }
};

interface TabData {
  [key: string]: any; // allow hyphenated keys like 'work-experience' and 'awards-achievements'
  basic?: any;
  education?: any;
  workExperience?: any;
  // runtime keys used by the tabs (dash-separated)
  'work-experience'?: any;
  skills?: any;
  'awards-achievements'?: any;
  awards?: any;
  certifications?: any;
}

interface CandidateProfileTabsProps {
  candidateUuid: string;
  isEditable?: boolean;
  currentUser: any;
  extractedResumeData?: any;
}

const CandidateProfileTabs = ({
  candidateUuid,
  isEditable = false,
  currentUser,
  extractedResumeData
}: CandidateProfileTabsProps) => {
  console.log('🚀 CandidateProfileTabs MOUNTED with props:', {
    candidateUuid,
    isEditable,
    hasCurrentUser: !!currentUser,
    extractedResumeData: extractedResumeData ? 'PRESENT' : 'NULL'
  });
  const [activeTab, setActiveTab] = useState('basic');
  const [tabData, setTabData] = useState<TabData>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [editMode, setEditMode] = useState<Record<string, boolean>>({});
  const [actualCandidateUuid, setActualCandidateUuid] = useState<string | null>(null);

  // Debug: Log every render with current state
  console.log('🔄 CandidateProfileTabs RENDER:', {
    activeTab,
    tabDataKeys: Object.keys(tabData),
    hasExtractedData: !!extractedResumeData,
    editModeKeys: Object.keys(editMode).filter(k => editMode[k])
  });

  // Get current user's candidate UUID if needed
  useEffect(() => {
    const fetchCurrentUserCandidate = async () => {
      if (candidateUuid === 'me' && currentUser) {
        try {
          const url = buildApiUrl('users/career-profile/me');
          const headers = getApiHeaders(true);
          
          const response = await fetch(url, { headers });
          const result = await response.json();
          
          if (result.success && result.data?.uuid) {
            // Use the profile UUID as the candidate UUID
            setActualCandidateUuid(result.data.uuid);
          } else {
            // If no candidate profile exists, create a default message
            toast.error('No candidate profile found. Please create your profile first.');
          }
        } catch (error) {
          console.error('Error fetching current user candidate:', error);
          toast.error('Failed to load your candidate profile');
        }
      } else {
        setActualCandidateUuid(candidateUuid);
      }
    };

    fetchCurrentUserCandidate();
  }, [candidateUuid, currentUser]);

  // Handle extracted resume data
  // Extract processing into a reusable function so it can be triggered by a UI action
  const applyExtractedData = (data: any) => {
    if (!data) return;

    console.log('🔥 PROCESSING EXTRACTED RESUME DATA (applyExtractedData):');
    console.log('📄 Raw extracted data:', JSON.stringify(data, null, 2));

    const updatedTabData: TabData = { ...tabData };

    // Prefer gemini_raw top-level structure when available
    const gr = data.gemini_raw || {};

    // Basic info mapping
    const basicInfo = gr.basic || data.basic_details || data.basic_info || {};
    const fullName = basicInfo.name || basicInfo.full_name || '';
    const nameParts = fullName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    updatedTabData.basic = {
      ...updatedTabData.basic,
      first_name: firstName,
      last_name: lastName,
      full_name: fullName,
      email: basicInfo.email || data.basic_info?.email || '',
      phone: basicInfo.phone || data.basic_info?.phone || '',
      linkedin_url: basicInfo.linkedin || basicInfo.linkedin_url || data.basic_info?.linkedin_url || '',
      github_url: basicInfo.github || data.basic_info?.github_url || '',
      current_location: basicInfo.location || basicInfo.address || data.basic_info?.address || '',
      current_title: basicInfo.title || data.basic_info?.current_title || (data.work_experience && data.work_experience[0]?.title) || '',
      current_company: (data.work_experience && data.work_experience[0]?.company) || data.basic_info?.current_company || basicInfo.company || '',
      job_looking_for: data.job_preferences || data.job_looking_for || '',
      summary: data.professional_summary || data.summary || gr.work_summary || ''
    };
    setEditMode(prev => ({ ...prev, basic: true }));

    // Education
    const educationData = (data.education?.length > 0) ? data.education : (gr.education || []);
    if (educationData && educationData.length > 0) {
      const mappedEducation = educationData.map((edu: any, index: number) => ({
        id: `temp-${index}`,
        uuid: edu.uuid || null,
        degree: edu.degree || edu.degreeType || edu.qualification || '',
        field_of_study: edu.fieldOfStudy || edu.field_of_study || edu.specialization || edu.stream || edu.branch || '',
        institution: edu.institution || edu.university || edu.school || edu.college || '',
        location: edu.location || '',
        start_date: edu.startDate || edu.start_date || edu.startYear || edu.from || '',
        end_date: edu.endDate || edu.end_date || edu.graduationYear || edu.endYear || edu.year || edu.to || '',
        is_current: edu.is_current || false,
        grade: edu.grade || edu.gpa || edu.percentage || edu.cgpa || '',
        description: edu.description || '',
        activities: edu.activities || ''
      }));
      updatedTabData.education = { education: mappedEducation, total_count: mappedEducation.length };
      setEditMode(prev => ({ ...prev, education: true }));
    }

    // Work experience
    const workExperienceData = (data.work_experience?.length > 0) ? data.work_experience : (gr.workExperience || gr.experience || []);
    if (workExperienceData && workExperienceData.length > 0) {
      const mappedWork = workExperienceData.map((exp: any, index: number) => {
        let startDate = exp.startDate || exp.start_date || '';
        let endDate = exp.endDate || exp.end_date || '';
        let isCurrent = exp.is_current || false;
        if (typeof endDate === 'string' && endDate.toLowerCase().includes('present')) {
          isCurrent = true;
        }
        return {
          uuid: exp.uuid || null,
          title: exp.title || exp.role || exp.position || '',
          company: exp.company || exp.organization || '',
          location: exp.location || '',
          start_date: startDate,
          end_date: isCurrent ? null : endDate,
          is_current: isCurrent,
          description: exp.description || exp.responsibilities || '',
          achievements: exp.achievements || '',
          technologies: exp.technologies || []
        };
      });
      updatedTabData['work-experience'] = { work_experience: mappedWork, total_count: mappedWork.length };
      setEditMode(prev => ({ ...prev, 'work-experience': true }));
    }

    // Certifications
    const certificationsData = (data.certifications?.length > 0) ? data.certifications : (gr.certifications || []);
    if (certificationsData && certificationsData.length > 0) {
      const mappedCerts = certificationsData.map((cert: any, index: number) => {
        if (typeof cert === 'string') {
          return {
            id: `temp-${index}`,
            certification_name: cert,
            issuing_organization: '',
            issue_date: '',
            expiry_date: '',
            credential_id: '',
            credential_url: '',
            description: '',
            is_active: true
          };
        } else {
          return {
            id: `temp-${index}`,
            certification_name: cert.title || cert.name || cert.certification_name || '',
            issuing_organization: cert.issuer || cert.issuing_organization || '',
            issue_date: cert.date || cert.issue_date || '',
            expiry_date: cert.expiry_date || '',
            credential_id: cert.credential_id || '',
            credential_url: cert.credential_url || '',
            description: cert.description || '',
            is_active: cert.is_active !== undefined ? cert.is_active : true
          };
        }
      });
      updatedTabData.certifications = { certifications: mappedCerts, total_count: mappedCerts.length };
      setEditMode(prev => ({ ...prev, certifications: true }));
    }

    // Skills
    let skillsArray: any[] = [];
    if (gr.skills && Array.isArray(gr.skills) && gr.skills.length > 0) {
      gr.skills.forEach((cat: any, ci: number) => {
        const category = cat.category || `Category ${ci}`;
        const list = cat.skills_list || cat.skills || [];
        if (Array.isArray(list)) {
          list.forEach((s: string, si: number) => skillsArray.push({
            uuid: `skill-${ci}-${si}`,
            skillName: s,
            skillCategory: category,
            proficiencyLevel: 'intermediate',
            yearsOfExperience: 0,
            isPrimarySkill: false,
            certificationRelated: false
          }));
        }
      });
    } else if (data.skills && Array.isArray(data.skills)) {
      skillsArray = data.skills.filter(Boolean).map((s: any, i: number) => ({ uuid: `skill-${i}`, skillName: typeof s === 'string' ? s : s.name || s.skillName || '', skillCategory: s.category || 'Technical', proficiencyLevel: s.level || 'intermediate', yearsOfExperience: s.years || 0, isPrimarySkill: false, certificationRelated: false }));
    }
    if (skillsArray.length > 0) {
      const skills_by_category = skillsArray.reduce((acc: Record<string, any[]>, s: any) => {
        const cat = s.skillCategory || 'Other';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(s);
        return acc;
      }, {} as Record<string, any[]>);

      updatedTabData.skills = {
        skills: skillsArray,
        skills_by_category,
        primary_skills: []
      };
      setEditMode(prev => ({ ...prev, skills: true }));
    }

    // Awards / accomplishments
    const combinedAwards: any[] = [];
    const grAwards = gr['awards-achievements'] || data['awards-achievements'] || data.awards || data.accomplishments || [];
    if (Array.isArray(grAwards) && grAwards.length > 0) {
      grAwards.forEach((a: any, i: number) => {
        if (typeof a === 'string') {
          combinedAwards.push({ uuid: null, title: a, issuer: '', date_received: '', description: a });
        } else {
          combinedAwards.push({ uuid: a.uuid || null, title: a.award || a.title || '', issuer: a.organization || a.issuer || '', date_received: a.year || a.date || '', description: a.description || '' });
        }
      });
    }
    if (combinedAwards.length > 0) {
      updatedTabData['awards-achievements'] = { awards_achievements: combinedAwards };
      setEditMode(prev => ({ ...prev, 'awards-achievements': true }));
    }

    console.log('🔄 About to set tabData with updatedTabData:', JSON.stringify(updatedTabData, null, 2));
    if (updatedTabData.skills) {
      console.log('🔍 Skills payload shape before setTabData:', JSON.stringify(updatedTabData.skills, null, 2));
    }
    setTabData(updatedTabData);
    const hasSkills = !!(updatedTabData.skills && ((updatedTabData.skills as any).skills || (updatedTabData.skills as any).length));
    console.log('📌 Applied skills count:', hasSkills ? ((updatedTabData.skills as any).skills ? (updatedTabData.skills as any).skills.length : (updatedTabData.skills as any).length) : 0);
    setActiveTab(hasSkills ? 'skills' : 'basic');

    toast.success('Resume data has been applied to your profile. Review and save the changes.');
    console.log('✅ FINAL UPDATED TAB DATA:', JSON.stringify(updatedTabData, null, 2));
  };

  // Call applyExtractedData when extractedResumeData changes
  useEffect(() => {
    if (extractedResumeData) {
      applyExtractedData(extractedResumeData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractedResumeData]);

  // Listen for explicit apply event from parent page (e.g., a button in preview panel)
  useEffect(() => {
    const handler = () => {
      if (extractedResumeData) applyExtractedData(extractedResumeData);
    };
    window.addEventListener('applyExtractedResume', handler as EventListener);
    return () => window.removeEventListener('applyExtractedResume', handler as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractedResumeData, tabData]);

  // Debug: Monitor tabData changes
  useEffect(() => {
    console.log('📊 TabData state changed:', {
      keys: Object.keys(tabData),
      hasBasic: !!tabData.basic,
      hasEducation: !!tabData.education,
      hasWorkExperience: !!tabData['work-experience'],
      hasSkills: !!tabData.skills,
      hasAwards: !!tabData['awards-achievements']
    });
  }, [tabData]);

  // Map tab names to route names
  const getRouteForTab = (tab: string): string => {
    const routeMapping: Record<string, string> = {
      'basic': 'users/career-profile/me',
      'education': 'education/me/profile',
      'work-experience': 'work-exp/me/profile/work-experience',
      'skills': 'candidates/me/profile/skills',
      'awards-achievements': 'awards/me/profile/awards-achievements',
      'certifications': 'certs/me/profile/certifications'
    };
    return routeMapping[tab] || `profile-tabs/me/profile/${tab}`;
  };

  // Map tab names to save/update routes
  const getSaveRouteForTab = (tab: string): string => {
    const saveRouteMapping: Record<string, string> = {
      'basic': 'users/career-profile/me',
      'education': 'education/me/profile/education',
      'work-experience': 'work-exp/me/profile/work-experience',
      'skills': 'candidates/me/profile/skills',
      'awards-achievements': 'awards/me/profile/awards-achievements',
      'certifications': 'certs/me/profile/certifications'
    };
    return saveRouteMapping[tab] || `profile-tabs/me/profile/${tab}`;
  };

  // Load tab data on demand
  const loadTabData = async (tab: string, force = false) => {
    // Skip loading if we already have data for this tab, are currently loading, don't have a candidate UUID, or have extracted data to process
    if (!force && (tabData[tab as keyof TabData] || loading[tab] || !actualCandidateUuid || extractedResumeData)) {
      console.log(`🚫 Skipping loadTabData for ${tab}: hasTabData=${!!tabData[tab as keyof TabData]}, loading=${loading[tab]}, hasCandidate=${!!actualCandidateUuid}, hasExtractedData=${!!extractedResumeData}`);
      return;
    }

    setLoading(prev => ({ ...prev, [tab]: true }));

    try {
      // Use new shorter routes when viewing own profile
      const isOwnProfile = candidateUuid === 'me';
      const url = isOwnProfile
        ? buildApiUrl(getRouteForTab(tab))
        : buildApiUrl(`candidates/${actualCandidateUuid}/profile/${tab}`);
      const headers = getApiHeaders(true);
      
      const response = await fetch(url, {
        method: 'GET',
        headers
      });
      
      const result = await response.json();
      
      if (result.success) {
        let processedData = result.data;
        
        // Special processing for skills tab to transform flat array into expected structure
        if (tab === 'skills' && result.data?.skills && Array.isArray(result.data.skills)) {
          const skills = result.data.skills;
          console.log('🔧 Processing skills data:', skills);
          
          // Group skills by category
          const skills_by_category = skills.reduce((acc: Record<string, any[]>, skill: any) => {
            const category = skill.skillCategory || 'Other';
            if (!acc[category]) acc[category] = [];
            acc[category].push(skill);
            return acc;
          }, {} as Record<string, any[]>);
          
          // Filter primary skills
          const primary_skills = skills.filter((skill: any) => skill.isPrimarySkill);
          
          processedData = {
            skills: skills,
            skills_by_category: skills_by_category,
            primary_skills: primary_skills,
            total_count: skills.length
          };
          
          console.log('🔧 Processed skills data structure:', processedData);
        }
        
        setTabData(prev => ({ ...prev, [tab]: processedData }));
      } else {
        toast.error(`Failed to load ${tab} data`);
      }
    } catch (error) {
      toast.error(`Error loading ${tab} data`);
      console.error(`Error loading ${tab}:`, error);
    } finally {
      setLoading(prev => ({ ...prev, [tab]: false }));
    }
  };

  // Load initial tab data - but only if we don't have extracted data for this tab
  useEffect(() => {
    // Skip loading if we have extracted data or if in edit mode for this tab
    const hasExtractedData = extractedResumeData;
    const isInEditMode = editMode[activeTab];

    console.log(`🔍 Loading tab ${activeTab}: hasExtractedData=${hasExtractedData}, isInEditMode=${isInEditMode}`);

    if (!hasExtractedData && !isInEditMode) {
      console.log(`📞 Loading data for tab: ${activeTab}`);
      loadTabData(activeTab);
    } else {
      console.log(`⏭️ Skipping load for tab: ${activeTab} (extracted data or edit mode)`);
    }
  }, [activeTab, actualCandidateUuid]);

  // Save tab data
  const saveTabData = async (tab: string, data: any) => {
    try {
      // Use proper save routes
      const isOwnProfile = candidateUuid === 'me';
      const url = isOwnProfile
        ? buildApiUrl(getSaveRouteForTab(tab))
        : buildApiUrl(`candidates/${actualCandidateUuid}/profile/${tab}`);
      const headers = getApiHeaders(true);

      // Detailed logging for debugging save issues (especially work-experience)
      console.log('🔁 saveTabData called', { tab, url, headers, data });

      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      });

      console.log('🔁 saveTabData response status:', response.status);

      let result: any = null;
      let rawText: string | null = null;
      try {
        rawText = await response.text();
        try {
          result = JSON.parse(rawText);
        } catch (errJson) {
          console.error('🔁 saveTabData: response not valid JSON:', errJson, rawText);
        }
        console.log('🔁 saveTabData response parsed:', result, 'rawText:', rawText);
      } catch (err) {
        console.error('🔁 Failed to read response text for saveTabData:', err);
      }

      if (response.ok && result && result.success) {
        toast.success(`${tab} information saved successfully`);
        setEditMode(prev => ({ ...prev, [tab]: false }));
        // Force reload tab data
        setTabData(prev => {
          const newTabData = { ...prev };
          delete newTabData[tab as keyof TabData];
          return newTabData;
        });
        // Force reload with updated data
        loadTabData(tab, true);
      } else {
        // Extract useful error text from common backend shapes
        let errMsg = `Failed to save ${tab} data`;
        if (result) {
          if (result.error) errMsg = result.error;
          else if (result.message) errMsg = result.message;
          else if (result.detail) {
            // detail may be a string or an object
            if (typeof result.detail === 'string') errMsg = result.detail;
            else if (result.detail.error) errMsg = result.detail.error;
            else if (result.detail.message) errMsg = result.detail.message;
            else errMsg = JSON.stringify(result.detail);
          }
        } else if (rawText) {
          errMsg = rawText;
        }

        // Shorten long messages for toasts but log full details to console
        const toastMsg = errMsg.length > 200 ? `${errMsg.slice(0, 197)}...` : errMsg;
        toast.error(toastMsg);
        console.error('🔁 saveTabData failed', { tab, url, status: response.status, result, rawText });
      }
    } catch (error) {
      toast.error(`Error saving ${tab} data`);
      console.error(`Error saving ${tab}:`, error);
    }
  };

  const tabs = [
    { value: 'basic', label: 'Basic Details', icon: User, endpoint: 'basic' },
    { value: 'work-experience', label: 'Work Experience', icon: Briefcase, endpoint: 'work-experience' },
    { value: 'education', label: 'Education', icon: GraduationCap, endpoint: 'education' },
    { value: 'skills', label: 'Skills', icon: Zap, endpoint: 'skills' },
    { value: 'awards-achievements', label: 'Awards', icon: Award, endpoint: 'awards-achievements' },
    { value: 'certifications', label: 'Certifications', icon: FileText, endpoint: 'certifications' },
  ];

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Candidate Profile</h1>
        <p className="text-muted-foreground">
          Comprehensive profile information organized in tabs for better navigation
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6 mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="flex items-center gap-2 py-3"
                onClick={() => loadTabData(tab.endpoint)}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden md:inline">{tab.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Basic Information Tab */}
        <TabsContent value="basic" className="space-y-6">
          <BasicInfoTab
            data={tabData.basic}
            loading={loading.basic}
            isEditable={isEditable}
            editMode={editMode.basic}
            onEdit={() => setEditMode(prev => ({ ...prev, basic: true }))}
            onSave={(data) => saveTabData('basic', data)}
            onCancel={() => setEditMode(prev => ({ ...prev, basic: false }))}
          />
        </TabsContent>

        {/* Education Tab */}
        <TabsContent value="education" className="space-y-6">
          <EducationTab
            data={tabData.education}
            loading={loading.education}
            isEditable={isEditable}
            editMode={editMode.education}
            onEdit={() => setEditMode(prev => ({ ...prev, education: true }))}
            onSave={(data) => saveTabData('education', data)}
            onCancel={() => setEditMode(prev => ({ ...prev, education: false }))}
            onRefresh={() => loadTabData('education', true)}
          />
        </TabsContent>

        {/* Work Experience Tab */}
        <TabsContent value="work-experience" className="space-y-6">
          <WorkExperienceTab
            data={tabData['work-experience']}
            loading={loading['work-experience']}
            isEditable={isEditable}
            editMode={editMode['work-experience']}
            onEdit={() => setEditMode(prev => ({ ...prev, 'work-experience': true }))}
            onSave={(data) => saveTabData('work-experience', data)}
            onCancel={() => setEditMode(prev => ({ ...prev, 'work-experience': false }))}
            onRefresh={() => loadTabData('work-experience', true)}
          />
        </TabsContent>

        {/* Skills Tab */}
        <TabsContent value="skills" className="space-y-6">
          <SkillsTab
            data={tabData.skills}
            loading={loading.skills}
            isEditable={isEditable}
            editMode={editMode.skills}
            onEdit={() => setEditMode(prev => ({ ...prev, skills: true }))}
            onSave={(data) => saveTabData('skills', data)}
            onCancel={() => setEditMode(prev => ({ ...prev, skills: false }))}
            candidateUuid={actualCandidateUuid}
            onRefresh={() => loadTabData('skills', true)}
          />
        </TabsContent>

        {/* Awards & Achievements Tab */}
        <TabsContent value="awards-achievements" className="space-y-6">
          <AwardsTab
            data={tabData['awards-achievements']}
            loading={loading['awards-achievements']}
            isEditable={isEditable}
            editMode={editMode['awards-achievements']}
            onEdit={() => setEditMode(prev => ({ ...prev, 'awards-achievements': true }))}
            onSave={(data) => saveTabData('awards-achievements', data)}
            onCancel={() => setEditMode(prev => ({ ...prev, 'awards-achievements': false }))}
            onRefresh={() => loadTabData('awards-achievements', true)}
          />
        </TabsContent>

        {/* Certifications Tab */}
        <TabsContent value="certifications" className="space-y-6">
          <CertificationsTab
            data={tabData.certifications}
            loading={loading.certifications}
            isEditable={isEditable}
            editMode={editMode.certifications}
            onEdit={() => setEditMode(prev => ({ ...prev, certifications: true }))}
            onSave={(data) => saveTabData('certifications', data)}
            onCancel={() => setEditMode(prev => ({ ...prev, certifications: false }))}
            onRefresh={() => loadTabData('certifications', true)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CandidateProfileTabs;